/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { Button } from "semantic-ui-react";
import { TextField } from "@mui/material";
import { selectAllResources, selectResourceById } from "../../slices/resourceCollection/resourceCollectionSlice";
import { selectEditMode } from "../../slices/editMode/editModeSlice";
import { selectTopSectionById, topSectionUpdated } from "../../slices/topSections/topSectionsSlice";
import jsonEquals from "../../functions/jsonEquals";
import adjustEmptyReason from "../compositionResourceFunctions/adjustEmptyReason";
import generateResourceTextNarrative from "../compositionResourceFunctions/generateResourceTextNarrative";
import CodeableConceptEntry from "../../datatypeComponents/CodeableConceptEntry";
import SectionEntryArrayEntry from "./SectionEntryArrayEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "../../datatypeComponents/ReferenceEntry";
import NarrativeEntry from "../../datatypeComponents/NarrativeEntry";
import EditSectionArrayModal from "./EditSectionArrayModal";
import { emptyReasonCodeableConceptSet, sectionCodeCodeableConceptSet } from "../../dictionaries/CodeSystemLookup";

const getNestedSection = (topSection, sectionPath) => { //Skips the top section (section at index 0) in the path
    if (sectionPath.length <= 1) {
        alert("UNEXPECTED SectionDisplay call for sectionPath length = " + sectionPath?.length);
        if (!sectionPath || sectionPath.length === 0) { return undefined; } else { return topSection; }
    }
    return sectionPath.slice(1).reduce((currentSection, index) => {
        return currentSection?.section?.[index];
    }, topSection); //Skips index 0 in its walk
}

const adjustEmptyReasonInLocation = (newTopSectionDetail, autoChangedEmptyReasonChanged,
    sectionPath) => {
    const revisedSection = getNestedSection(newTopSectionDetail.revisedSection, sectionPath);
    let previousFinalValue = null;
    if (revisedSection) {
        previousFinalValue = revisedSection.emptyReason;
        if (autoChangedEmptyReasonChanged) {
            revisedSection.emptyReason = autoChangedEmptyReasonChanged; //this will change newTopSectionDetail but not previousFinalValue
        } else if (revisedSection.emptyReason) {
            delete revisedSection.emptyReason; //this will change newTopSectionDetail but not previousFinalValue
        }
    }
    if (autoChangedEmptyReasonChanged !== undefined && (previousFinalValue || autoChangedEmptyReasonChanged) &&
        !jsonEquals(previousFinalValue, autoChangedEmptyReasonChanged)) {
        const sectionPathString = sectionPath.map(index => `section[${index}]`).join('.'); //Like: sectionPathString = "section[" + sectionPath[0] + "].section[" + sectionPath[1] + "].section[" + sectionPath[2] + "].section[" + sectionPath[3] + "].section[" + sectionPath[4] + "].section[" + sectionPath[5] + "]";
        console.log("resourceUpdate for Composition." + sectionPathString + ".emptyReason");
    } else {
        autoChangedEmptyReasonChanged = "no change";
    }
    return autoChangedEmptyReasonChanged;
};

const SectionDisplay = ({ sectionPath, foi, resourceReferenceMap, setParentSectionState, isM11ReportProfile,
    containedResourceDictionary
}) => {
    const topSection = useSelector(state => selectTopSectionById(state, sectionPath[0]));
    const compositionId = topSection?.compositionId;
    const compositionType = topSection?.productFamily || "Composition";
    const compositionTitle = resourceReferenceMap?.compositionTitle;
    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const resourceCollection = useSelector(state => selectAllResources(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));

    const [sectionLoaded, setSectionLoaded] = useState({});
    const [expandState, setExpandState] = useState(false);
    const [editSectionModal, setEditSectionModal] = useState({ "openModal": false });
    const [sectionState, setSectionState] = useState({});

    useEffect(() => {
        if (compositionTitle && !sectionLoaded.compositionTitle && topSection?.revisedSection) {
            const section = getNestedSection(topSection.revisedSection, sectionPath);
            const sectionCode = section?.code?.coding?.[0]?.code || section?.code?.text;
            let generatedNarrative;
            if (section?.entry?.length === 1 && section.entry[0]?.reference?.split("/").length === 2 &&
                !isNaN(section.entry[0].reference.split("/")[1])) {
                let resourceFoi = section.entry[0].reference.split("/")[1];
                let resourceDictionary = {};
                for (const entry of resourceCollection) {
                    resourceDictionary[entry.id] = entry;
                }
                if (resourceDictionary[resourceFoi] && resourceDictionary[resourceFoi].revisedResourceJson) {
                    let resourceJson = resourceDictionary[resourceFoi].revisedResourceJson;
                    generatedNarrative = generateResourceTextNarrative(resourceJson);
                }
            }
            const sectionPathString = sectionPath.map(index => `section[${index}]`).join('.');
            setSectionLoaded({
                "section": section, "sectionCode": sectionCode,
                "sectionPathString": sectionPathString, "sectionPath": sectionPath,
                "compositionType": compositionType, "compositionTitle": compositionTitle,
                "generatedNarrative": generatedNarrative, "focus": section?.focus
            });
        }
    }, [compositionTitle, sectionPath, topSection?.revisedSection]);

    useEffect(() => {
        if (sectionLoaded.section) {
            let section = sectionLoaded.section;
            setSectionState({
                "editTitle": false,
                "title": section.title || "",
                "code": section.code || null,
                "focus": section.focus || null,
                "text": section.text || {
                    "status": "empty",
                    "div": '<div xmlns="http://www.w3.org/1999/xhtml"><p>[No data.]</p></div>'
                },
                "entry": section.entry || [],
                "emptyReason": section.emptyReason || null,
                "section": section?.section || []
            });
        }
    }, [sectionLoaded]);

    useEffect(() => {
        if (editMode && sectionLoaded.section && compositionId && sectionState.title !== undefined) {
            const previousFinalValue = getNestedSection(topSection.revisedSection, sectionPath)?.title ?? null;
            if (previousFinalValue !== sectionState.title) {
                const sectionPathString = sectionPath.map(index => `section[${index}]`).join('.');
                const newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
                const revisedSection = getNestedSection(newTopSectionDetail.revisedSection, sectionPath);
                revisedSection.title = sectionState.title;
                console.log("resourceUpdate for Composition." + sectionPathString + ".title");
                dispatch(topSectionUpdated(newTopSectionDetail));
                setParentSectionState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionPath[sectionPath.length - 1]].text = sectionState.text;
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
            }
        }
    }, [sectionState.title]);

    useEffect(() => {
        if (editMode && sectionLoaded.section && compositionId && sectionState.code !== undefined) {
            const previousFinalValue = getNestedSection(topSection.revisedSection, sectionPath)?.code ?? null;
            if (!jsonEquals(previousFinalValue, sectionState.code)) {
                const newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
                const sectionPathString = sectionPath.map(index => `section[${index}]`).join('.');
                const revisedSection = getNestedSection(newTopSectionDetail.revisedSection, sectionPath);
                revisedSection.code = sectionState.code;
                console.log("resourceUpdate for Composition." + sectionPathString + ".code");
                dispatch(topSectionUpdated(newTopSectionDetail));
                setParentSectionState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionPath[sectionPath.length - 1]].code = sectionState.code;
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
            }
        }
    }, [sectionState.code]);

    useEffect(() => {
        if (editMode && sectionLoaded.section && compositionId) {
            const newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            const revisedSection = getNestedSection(newTopSectionDetail.revisedSection, sectionPath);
            let previousFinalValue = null;
            if (revisedSection) {
                previousFinalValue = revisedSection.focus;
                revisedSection.focus = sectionState.focus;
            }
            const previousInitialValue = getNestedSection(newTopSectionDetail.initialSection, sectionPath)?.focus || null;
            if (sectionState.focus !== undefined
                && (sectionState.focus !== null || previousInitialValue !== null)
                && !jsonEquals(previousFinalValue, sectionState.focus)) {
                const sectionPathString = sectionPath.map(index => `section[${index}]`).join('.');
                console.log("resourceUpdate for Composition." + sectionPathString + ".focus");
                dispatch(topSectionUpdated(newTopSectionDetail));
                setParentSectionState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionPath[sectionPath.length - 1]].focus = sectionState.focus;
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
            }
        }
    }, [sectionState.focus]);

    useEffect(() => {
        if (editMode && sectionLoaded.section && compositionId && sectionState.text !== undefined) {
            const previousFinalValue = getNestedSection(topSection.revisedSection, sectionPath)?.text ?? null;
            if ((previousFinalValue !== undefined || sectionState.text.status !== "empty")
                && !jsonEquals(previousFinalValue, sectionState.text)) {
                const newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
                const sectionPathString = sectionPath.map(index => `section[${index}]`).join('.');
                const revisedSection = getNestedSection(newTopSectionDetail.revisedSection, sectionPath);
                revisedSection.text = sectionState.text;
                console.log("resourceUpdate for Composition." + sectionPathString + ".text");
                let autoChangedEmptyReasonChanged = adjustEmptyReason(sectionState, setSectionState);
                if (autoChangedEmptyReasonChanged !== "no change") {
                    autoChangedEmptyReasonChanged = adjustEmptyReasonInLocation(newTopSectionDetail,
                        autoChangedEmptyReasonChanged, sectionPath);
                }
                dispatch(topSectionUpdated(newTopSectionDetail));
                setParentSectionState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionPath[sectionPath.length - 1]].text = sectionState.text;
                    if (autoChangedEmptyReasonChanged !== "no change") {
                        newSection[sectionPath[sectionPath.length - 1]].emptyReason = autoChangedEmptyReasonChanged;
                    }
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
            }
        }
    }, [sectionState.text]);

    useEffect(() => {
        if (editMode && sectionLoaded.section && compositionId && sectionState.entry !== undefined) {
            const previousInitialValue = getNestedSection(topSection.initialSection, sectionPath)?.entry || null;
            if (previousInitialValue || !Array.isArray(sectionState.entry) || (sectionState.entry.length > 1 || sectionState.entry[0])) {
                const previousFinalValue = getNestedSection(topSection.revisedSection, sectionPath)?.entry || null;
                if (!jsonEquals(previousFinalValue, sectionState.entry)) {
                    const sectionPathString = sectionPath.map(index => `section[${index}]`).join('.');
                    const newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
                    const revisedSection = getNestedSection(newTopSectionDetail.revisedSection, sectionPath);
                    revisedSection.entry = sectionState.entry;
                    console.log("resourceUpdate for Composition." + sectionPathString + ".entry");
                    let autoChangedEmptyReasonChanged = adjustEmptyReason(sectionState, setSectionState);
                    if (autoChangedEmptyReasonChanged !== "no change") {
                        autoChangedEmptyReasonChanged = adjustEmptyReasonInLocation(newTopSectionDetail,
                            autoChangedEmptyReasonChanged, sectionPath);
                    }
                    dispatch(topSectionUpdated(newTopSectionDetail));
                    setParentSectionState(prevState => {
                        let newSection = JSON.parse(JSON.stringify(prevState.section));
                        newSection[sectionPath[sectionPath.length - 1]].entry = sectionState.entry;
                        if (autoChangedEmptyReasonChanged !== "no change") {
                            newSection[sectionPath[sectionPath.length - 1]].emptyReason = autoChangedEmptyReasonChanged;
                        }
                        return {
                            ...prevState,
                            section: newSection
                        }
                    });
                }
            }
        }
    }, [sectionState.entry]);

    useEffect(() => {
        if (editMode && sectionLoaded.section && compositionId && sectionState?.section !== undefined) {
            const newTopSectionDetail = JSON.parse(JSON.stringify(topSection));
            const revisedSection = getNestedSection(newTopSectionDetail.revisedSection, sectionPath);
            let previousFinalValue = null;
            if (revisedSection) {
                previousFinalValue = revisedSection.section;
                revisedSection.section = sectionState.section;  //this will change newTopSectionDetail but not previousFinalValue
            }
            const previousInitialValue = getNestedSection(newTopSectionDetail.initialSection, sectionPath)?.section || null;
            if (
                (previousInitialValue || !Array.isArray(sectionState.section) || (sectionState.section.length > 1 || sectionState.section[0])) &&
                !jsonEquals(previousFinalValue?.map(section => section.code) || null, sectionState.section.map(section => section.code))) {
                const sectionPathString = sectionPath.map(index => `section[${index}]`).join('.');
                console.log("resourceUpdate for Composition." + sectionPathString + ".section");
                let autoChangedEmptyReasonChanged = adjustEmptyReason(sectionState, setSectionState);
                if (autoChangedEmptyReasonChanged !== "no change") {
                    autoChangedEmptyReasonChanged = adjustEmptyReasonInLocation(newTopSectionDetail,
                        autoChangedEmptyReasonChanged, sectionPath);
                }
                dispatch(topSectionUpdated(newTopSectionDetail));
                setParentSectionState(prevState => {
                    let newSection = JSON.parse(JSON.stringify(prevState.section));
                    newSection[sectionPath[sectionPath.length - 1]].section = sectionState.section;
                    if (autoChangedEmptyReasonChanged !== "no change") {
                        newSection[sectionPath[sectionPath.length - 1]].emptyReason = autoChangedEmptyReasonChanged;
                    }
                    return {
                        ...prevState,
                        section: newSection
                    }
                });
            }
        }
    }, [sectionState.section]);

    return <>
        {(sectionLoaded.section &&
            (sectionState.title || sectionState.text || sectionState.entry || sectionState.section || sectionState.emptyReason)
        ) &&
            <div id={sectionLoaded.sectionCode}>
                <div>
                    {editMode ?
                        <>
                            {
                                sectionState.editTitle ?
                                    <>
                                        <TextField style={{ width: "100%" }} className="inputField"
                                            type='text' variant='outlined'
                                            placeholder='Enter section title.' value={sectionState.title}
                                            onKeyUp={(event) => {
                                                if (event.key === 'Enter') {
                                                    setSectionState(prevState => {
                                                        return {
                                                            ...prevState,
                                                            "editTitle": false
                                                        }
                                                    });
                                                }
                                            }}
                                            onChange={(e) => {
                                                setSectionState(prevState => {
                                                    return {
                                                        ...prevState,
                                                        "title": e.target.value || ""
                                                    }
                                                });
                                            }} />
                                        <span className={"unselectable"} style={{ cursor: "pointer", color: "blue" }}
                                            onClick={() => { setExpandState(!expandState); }}
                                        >{expandState ? " ▼ collapse" : " ► expand"}
                                        </span>
                                    </>
                                    :
                                    <h3>
                                        <span>{sectionState.title || sectionLoaded.section.title || "Untitled Section"}</span>
                                        <span>  <button type="button" style={{ marginLeft: "12px" }}
                                            onClick={() => {
                                                setSectionState(prevState => {
                                                    return {
                                                        ...prevState,
                                                        "editTitle": true
                                                    }
                                                });
                                            }} >✎ Edit title</button>
                                        </span>
                                        <span className={"unselectable"} style={{ cursor: "pointer", color: "blue" }}
                                            onClick={() => { setExpandState(!expandState); }}
                                        >{expandState ? " ▼ collapse" : " ► expand"}
                                        </span>
                                    </h3>
                            }
                        </>
                        :
                        <h3>
                            {sectionState.title || sectionLoaded.section.title || "Untitled Section"}
                            <span className={"unselectable"} style={{ cursor: "pointer", color: "blue" }}
                                onClick={() => { setExpandState(!expandState); }}
                            >{expandState ? " ▼ collapse" : " ► expand"}
                            </span>
                        </h3>
                    }
                    {expandState && <div style={{ marginLeft: "24px" }}>
                        <div>
                            {editMode ?
                                <NarrativeEntry elementName="text"
                                    startingValue={sectionState.text}
                                    calledFrom={isM11ReportProfile && "M11Report"}
                                    generatedNarrative={sectionLoaded.generatedNarrative}
                                    setResourceState={setSectionState} />
                                :
                                <>{(sectionLoaded.section.text?.div &&
                                    sectionLoaded.section.text.status !== "empty") &&
                                    <>
                                        <ReactMarkdown children={sectionState?.text?.div || sectionLoaded.section.text.div}
                                            rehypePlugins={[rehypeRaw]} />
                                        <br />
                                    </>}
                                </>
                            }
                        </div>
                        {editMode &&
                            <CodeableConceptEntry elementName="code" fieldLabel="Section Code"
                                startingValue={sectionState.code}
                                codeableConceptLevelValueSet={sectionCodeCodeableConceptSet}
                                setResourceState={setSectionState} startCollapsed />
                        }
                        {
                            editMode ?
                                <>
                                    <ReferenceEntry elementName="focus" fieldLabel="Section Focus"
                                        containedResourceDictionary={containedResourceDictionary}
                                        startingValue={sectionState.focus}
                                        setResourceState={setSectionState} deletable startCollapsed />
                                </>
                                :
                                <>
                                    {sectionLoaded.section.focus && <div>
                                        <div>
                                            <b>Section focus: </b>
                                            <DisplayFromFHIRReference reference={sectionState?.focus || sectionLoaded.section.focus}
                                                containedResourceJson={sectionState?.focus ?
                                                    sectionState.focus.reference && containedResourceDictionary[sectionState.focus.reference]
                                                    :
                                                    sectionLoaded.section.focus.reference && containedResourceDictionary[sectionLoaded.section.focus.reference]} />
                                        </div>
                                        <p></p>
                                    </div>}
                                </>
                        }
                        {(sectionLoaded.section.entry?.length > 0 || sectionLoaded.section.section?.length > 0 || editMode) &&
                            <>
                                <p></p>
                                <p><b>This section contains:</b></p>
                            </>
                        }
                        <div style={{ marginLeft: "24px" }}>
                            {editMode ?
                                <SectionEntryArrayEntry sectionState={sectionState}
                                    setSectionState={setSectionState} sectionLoaded={sectionLoaded}
                                    foi={foi} revisedResourceJson={parentResourceDetail?.revisedResourceJson}
                                    resourceReferenceMap={resourceReferenceMap} />
                                :
                                <>
                                    {(sectionLoaded.section.entry?.length > 0) &&
                                        <>
                                            {sectionLoaded.section.entry.map((entry, entryIndex) => {
                                                return <span key={entryIndex}>
                                                    <p></p>
                                                    <DisplayFromFHIRReference reference={entry}
                                                        containedResourceJson={entry?.reference && containedResourceDictionary[entry.reference]} />

                                                </span>
                                            })}
                                        </>}
                                </>
                            }
                            {editMode && <div>
                                <Button className="formButton" style={{ margin: "0px", /*color: "#000000", */ }}
                                    content="Add/Delete/Reorganize Sections" compact
                                    onClick={() => { setEditSectionModal({ "openModal": true }) }} />
                                <br />
                            </div>}
                            {editSectionModal.openModal &&
                                <EditSectionArrayModal editSectionModal={editSectionModal}
                                    setEditSectionModal={setEditSectionModal}
                                    sectionState={sectionState}
                                    setSectionState={setSectionState} />}
                            {(sectionLoaded.section.section?.length > 0) &&
                                sectionLoaded.section.section.map((subsection, subsectionIndex) => {
                                    return <div key={subsectionIndex}>
                                        <SectionDisplay sectionPath={sectionPath.concat([subsectionIndex])}
                                            containedResourceDictionary={containedResourceDictionary}
                                            isM11ReportProfile={isM11ReportProfile}
                                            foi={foi} resourceReferenceMap={resourceReferenceMap}
                                            setParentSectionState={setSectionState} />
                                    </div>
                                })}
                            {editMode ?
                                <>
                                    <CodeableConceptEntry elementName="emptyReason" fieldLabel="Empty Reason"
                                        startingValue={sectionState.emptyReason}
                                        codeableConceptLevelValueSet={emptyReasonCodeableConceptSet}
                                        setResourceState={setSectionState} deletable startCollapsed />
                                </>
                                :
                                <>
                                    {sectionLoaded.section.emptyReason && <div>
                                        <p>Section is empty: {sectionState?.emptyReason ?
                                            <>{sectionState.emptyReason.coding?.[0]?.display} {sectionState.emptyReason.text}</>
                                            :
                                            <>{sectionLoaded.section.emptyReason.coding?.[0]?.display} {sectionLoaded.section.emptyReason.text}</>}
                                        </p>
                                    </div>}
                                </>
                            }
                        </div>
                        <br />
                    </div>}
                </div>
            </div>}
    </>
}

export default SectionDisplay;